import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Loader, 
  Shield, 
  Database, 
  Wifi, 
  User,
  RefreshCw,
  LogIn,
  ArrowRight
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import { Link } from 'react-router-dom'

const ConnectionTestPage = () => {
  const { user, signIn } = useAuth()
  const [testResults, setTestResults] = useState([])
  const [isRunning, setIsRunning] = useState(false)
  const [currentTest, setCurrentTest] = useState('')
  const [showLoginForm, setShowLoginForm] = useState(false)
  const [loginData, setLoginData] = useState({ email: '', password: '' })
  const [loginError, setLoginError] = useState('')

  const addResult = (name, status, message, details = null) => {
    setTestResults(prev => [...prev, {
      name,
      status, // 'success', 'error', 'warning', 'info'
      message,
      details,
      timestamp: new Date().toLocaleTimeString()
    }])
  }

  const runConnectionTests = async () => {
    setIsRunning(true)
    setTestResults([])
    
    try {
      // Test 1: Network Connectivity
      setCurrentTest('Testing network connectivity...')
      try {
        const response = await fetch('https://xtqjzmkqvqjqjqjqjqjq.supabase.co/rest/v1/', {
          method: 'HEAD'
        })
        addResult(
          'Network Connectivity',
          'success',
          'Successfully connected to Supabase',
          `Status: ${response.status}`
        )
      } catch (error) {
        addResult(
          'Network Connectivity',
          'error',
          'Failed to connect to Supabase',
          error.message
        )
      }

      // Test 2: Authentication Status
      setCurrentTest('Checking authentication status...')
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      
      if (session && session.user) {
        addResult(
          'Authentication Status',
          'success',
          `Authenticated as ${session.user.email}`,
          `User ID: ${session.user.id}`
        )
      } else {
        addResult(
          'Authentication Status',
          'warning',
          'No authenticated user session',
          'Login required to access protected data'
        )
      }

      // Test 3: Database Connection (if authenticated)
      if (session && session.user) {
        setCurrentTest('Testing database access...')
        
        // Test user profile access
        try {
          const { data: profile, error: profileError } = await supabase
            .from('users')
            .select('name, agency, role')
            .eq('id', session.user.id)
            .single()
          
          if (profileError) {
            addResult(
              'User Profile Access',
              'error',
              'Failed to access user profile',
              profileError.message
            )
          } else {
            addResult(
              'User Profile Access',
              'success',
              'Successfully accessed user profile',
              `Name: ${profile.name}, Agency: ${profile.agency}`
            )
          }
        } catch (error) {
          addResult(
            'User Profile Access',
            'error',
            'Profile access failed',
            error.message
          )
        }

        // Test incident data access
        try {
          const { data: incidents, error: incidentError } = await supabase
            .from('incidents')
            .select('id, title, severity')
            .limit(5)
          
          if (incidentError) {
            if (incidentError.code === 'PGRST116') {
              addResult(
                'Incident Data Access',
                'info',
                'No incidents found (normal for new accounts)',
                'Table exists and is accessible'
              )
            } else {
              addResult(
                'Incident Data Access',
                'error',
                'Failed to access incident data',
                incidentError.message
              )
            }
          } else {
            addResult(
              'Incident Data Access',
              'success',
              `Successfully accessed incident data (${incidents.length} records)`,
              incidents.length > 0 ? `Latest: ${incidents[0].title}` : 'No incidents yet'
            )
          }
        } catch (error) {
          addResult(
            'Incident Data Access',
            'error',
            'Incident access failed',
            error.message
          )
        }
      } else {
        addResult(
          'Database Access',
          'warning',
          'Skipped - authentication required',
          'Login to test data access'
        )
      }

      // Test 4: Real-time Connection
      setCurrentTest('Testing real-time connection...')
      try {
        const channel = supabase.channel('connection-test')
        
        const subscriptionPromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Real-time connection timeout'))
          }, 5000)
          
          channel
            .on('presence', { event: 'sync' }, () => {
              clearTimeout(timeout)
              resolve('Connected')
            })
            .subscribe((status) => {
              if (status === 'SUBSCRIBED') {
                clearTimeout(timeout)
                resolve('Connected')
              } else if (status === 'CHANNEL_ERROR') {
                clearTimeout(timeout)
                reject(new Error('Channel error'))
              }
            })
        })
        
        await subscriptionPromise
        addResult(
          'Real-time Connection',
          'success',
          'Real-time connection established',
          'WebSocket connection working'
        )
        
        supabase.removeChannel(channel)
      } catch (error) {
        addResult(
          'Real-time Connection',
          'warning',
          'Real-time connection issues',
          error.message
        )
      }

    } catch (error) {
      addResult(
        'Test Suite',
        'error',
        'Test suite failed',
        error.message
      )
    } finally {
      setIsRunning(false)
      setCurrentTest('')
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoginError('')
    
    const { data, error } = await signIn(loginData.email, loginData.password)
    
    if (error) {
      setLoginError(error.message)
    } else {
      setShowLoginForm(false)
      setLoginData({ email: '', password: '' })
      // Re-run tests after successful login
      setTimeout(runConnectionTests, 1000)
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'error': return <XCircle className="w-5 h-5 text-red-600" />
      case 'warning': return <AlertCircle className="w-5 h-5 text-yellow-600" />
      case 'info': return <AlertCircle className="w-5 h-5 text-blue-600" />
      default: return <AlertCircle className="w-5 h-5 text-gray-600" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'border-green-200 bg-green-50'
      case 'error': return 'border-red-200 bg-red-50'
      case 'warning': return 'border-yellow-200 bg-yellow-50'
      case 'info': return 'border-blue-200 bg-blue-50'
      default: return 'border-gray-200 bg-gray-50'
    }
  }

  useEffect(() => {
    // Auto-run tests on component mount
    runConnectionTests()
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-cyber-600 to-gov-600 px-6 py-8">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Wifi className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  Connection Diagnostic Test
                </h1>
                <p className="text-cyber-100 mt-1">
                  Verify your connection to CyberGov services
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Current User Status */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {user ? `Logged in as ${user.email}` : 'Not authenticated'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {user ? 'All features available' : 'Limited access - login required'}
                    </p>
                  </div>
                </div>
                {!user && (
                  <button
                    onClick={() => setShowLoginForm(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-cyber-600 text-white rounded-lg hover:bg-cyber-700 transition-colors"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Login</span>
                  </button>
                )}
              </div>
            </div>

            {/* Login Form Modal */}
            {showLoginForm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                onClick={() => setShowLoginForm(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-lg font-semibold mb-4">Login to Test Full Access</h3>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={loginData.email}
                        onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <input
                        type="password"
                        value={loginData.password}
                        onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                        required
                      />
                    </div>
                    {loginError && (
                      <p className="text-red-600 text-sm">{loginError}</p>
                    )}
                    <div className="flex space-x-3">
                      <button
                        type="submit"
                        className="flex-1 bg-cyber-600 text-white py-2 rounded-lg hover:bg-cyber-700 transition-colors"
                      >
                        Login
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowLoginForm(false)}
                        className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                  <div className="mt-4 text-center">
                    <Link
                      to="/register"
                      className="text-cyber-600 hover:text-cyber-700 text-sm"
                      onClick={() => setShowLoginForm(false)}
                    >
                      Don't have an account? Sign up
                    </Link>
                  </div>
                </motion.div>
              </motion.div>
            )}

            {/* Test Controls */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Test Results</h2>
                <p className="text-sm text-gray-600">
                  {isRunning ? currentTest : 'Click refresh to run tests again'}
                </p>
              </div>
              <button
                onClick={runConnectionTests}
                disabled={isRunning}
                className="flex items-center space-x-2 px-4 py-2 bg-gov-600 text-white rounded-lg hover:bg-gov-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isRunning ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                <span>{isRunning ? 'Running...' : 'Refresh Tests'}</span>
              </button>
            </div>

            {/* Test Results */}
            <div className="space-y-3">
              {testResults.map((result, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-4 rounded-lg border ${getStatusColor(result.status)}`}
                >
                  <div className="flex items-start space-x-3">
                    {getStatusIcon(result.status)}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-gray-900">{result.name}</h3>
                        <span className="text-xs text-gray-500">{result.timestamp}</span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{result.message}</p>
                      {result.details && (
                        <p className="text-xs text-gray-600 mt-1 font-mono bg-white/50 px-2 py-1 rounded">
                          {result.details}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Summary and Next Steps */}
            {testResults.length > 0 && !isRunning && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 p-6 bg-gradient-to-r from-cyber-50 to-gov-50 rounded-lg"
              >
                <h3 className="font-semibold text-gray-900 mb-3">Summary & Next Steps</h3>
                
                {user ? (
                  <div className="space-y-2">
                    <p className="text-green-700 font-medium">✅ Authentication: Working</p>
                    <p className="text-sm text-gray-700">
                      Your connection is properly configured. You can access all CyberGov features.
                    </p>
                    <Link
                      to="/dashboard"
                      className="inline-flex items-center space-x-2 mt-3 px-4 py-2 bg-cyber-600 text-white rounded-lg hover:bg-cyber-700 transition-colors"
                    >
                      <span>Go to Dashboard</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-yellow-700 font-medium">⚠️ Authentication Required</p>
                    <p className="text-sm text-gray-700">
                      To resolve the connection issue and access incident data, please log in with your government credentials.
                    </p>
                    <div className="flex space-x-3 mt-3">
                      <button
                        onClick={() => setShowLoginForm(true)}
                        className="px-4 py-2 bg-cyber-600 text-white rounded-lg hover:bg-cyber-700 transition-colors"
                      >
                        Login Now
                      </button>
                      <Link
                        to="/register"
                        className="px-4 py-2 bg-gov-600 text-white rounded-lg hover:bg-gov-700 transition-colors"
                      >
                        Create Account
                      </Link>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default ConnectionTestPage